import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import KeniOrb from '@/components/wrapped/KeniOrb';
import ChatBubble from '@/karehero-app/tell-us-once/intake/ChatBubble';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import { type ChatQuestion } from '@/karehero-app/tell-us-once/intake/content';

// Option 1's chat: the opening questions were screens of their own, so the
// conversation opens straight on the invitation. Talk or type, nothing scripted.
//
// Deliberately a copy of ChatV2 rather than a shared component with a flag. The
// two options are being compared, so either has to be free to change without
// moving the other.
type Msg = { from: 'keni' | 'user'; text: string };

const ChatV1 = ({
  questions,
  onExit,
  onComplete,
}: {
  questions: ChatQuestion[];
  onExit: () => void;
  onComplete?: () => void;
}) => {
  const [index, setIndex] = useState(0);
  const [transcript, setTranscript] = useState<Msg[]>([
    { from: 'keni', text: questions[0].ask },
  ]);
  const [text, setText] = useState('');

  // The transcript scrolls, not the message: scrollIntoView would drag the
  // flows page along with it.
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [transcript]);

  const q = questions[index];
  const done = !q;

  const answer = (value: string) => {
    if (!value || !q) return;
    const next = index + 1;
    setTranscript((prev) => [
      ...prev,
      { from: 'user', text: value },
      ...(questions[next]
        ? [{ from: 'keni' as const, text: questions[next].ask }]
        : []),
    ]);
    setText('');
    setIndex(next);
    if (!questions[next]) onComplete?.();
  };

  return (
    <ScreenRoot>
      <StatusBar />

      <Stack sx={{ alignItems: 'center', px: 2, py: 1.5 }} direction='row'>
        <IconButton aria-label='Leave chat' edge='start' onClick={onExit}>
          <Icon icon={'xmark' as never} fontSize={16} />
        </IconButton>
      </Stack>
      <Divider />

      <Box
        ref={scrollerRef}
        sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: '72px' }}
      >
        <Stack spacing={1.5}>
          {transcript.map((m, i) => (
            <ChatBubble key={i} from={m.from} text={m.text} />
          ))}
        </Stack>
      </Box>

      {/* The orb straddles the top edge, with the input always beneath it. */}
      <Box
        sx={{
          position: 'relative',
          px: 3,
          pb: 2,
          pt: '52px',
          borderTop: 1,
          borderColor: 'custom.neutral.200',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <KeniOrb size={80} state='listening' />
        </Box>

        <Stack spacing={1.5}>
          <Typography
            sx={{ textAlign: 'center', color: 'custom.coral.700' }}
            variant='caption'
          >
            Listening…
          </Typography>

          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
            <TextField
              size='small'
              variant='outlined'
              value={text}
              disabled={done}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') answer(text.trim());
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': { borderRadius: `${radius.xxl}px` },
                '& .MuiOutlinedInput-input': { pl: 2 },
              }}
            />
            <IconButton
              aria-label='Send'
              disabled={done}
              onClick={() => answer(text.trim())}
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                bgcolor: 'custom.coral.600',
                color: 'common.white',
                '&:hover': { bgcolor: 'custom.coral.700' },
                '&.Mui-disabled': {
                  bgcolor: 'custom.neutral.200',
                  color: 'common.white',
                },
              }}
            >
              <Icon icon={'paper-plane-top' as never} fontSize={16} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>
      {/* The safe area belongs to the bar above it, so it takes its colour. */}
      <Box sx={{ flexShrink: 0, bgcolor: 'background.paper' }}>
        <HomeIndicator />
      </Box>
    </ScreenRoot>
  );
};

export default ChatV1;
